import { useRef, useLayoutEffect, useState, useContext } from 'react';
import './expandablePanel.css';
import { DesignSystemContext } from '../../context/DesignSystemContext';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { expandablePanelI18n } from './i18n';

const ExpandablePanel = ({
  open = false,
  children,
  className = '',
  showDivider = true,
  showActions = false,
  applyLabel,
  clearLabel,
  cancelLabel,
  onApply,
  onClear,
  onClose,
}) => {
  const { t } = useScopedTranslation(expandablePanelI18n);
  const resolvedApplyLabel = applyLabel ?? t('panel_apply');
  const resolvedClearLabel = clearLabel ?? t('panel_clear');
  const resolvedCancelLabel = cancelLabel ?? t('panel_cancel');
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  const designSystem = useContext(DesignSystemContext);
  const Button = designSystem?.Button;

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    if (open) {
      const fullHeight = el.scrollHeight;
      setHeight(`${fullHeight}px`);

      const timer = setTimeout(() => {
        setHeight('auto');
      }, 400);

      return () => clearTimeout(timer);
    } else {
      const currentHeight = el.scrollHeight;
      setHeight(`${currentHeight}px`);
      requestAnimationFrame(() => setHeight('0px'));
    }
  }, [open, children]);

  const handleApplyClick = () => {
    if (onApply) {
      onApply();
    }
    // closing the panel is part of the component's behavior:
    if (onClose) {
      onClose();
    }
  };

  const handleClearClick = () => {
    if (onClear) {
      onClear();
    }
  };

  const handleCancelClick = () => {
    // cancel just closes the panel
    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      className={`expandable-panel-wrapper  ${open ? 'open mb-4' : ''} ${className}`}
      style={{
        maxHeight: height,
        opacity: open ? 1 : 0,
        transition: 'max-height 0.4s ease, opacity 0.3s ease',
      }}
    >
      <div ref={contentRef} className='expandable-panel'>
        <div className='expandable-panel-content'>
          {showDivider && <hr className='expandable-divider' />}
          {children}
          {showActions && Button && (
            <div className='expandable-panel-actions'>
              <div className='d-flex justify-content-between'>
                <div className='d-flex'>
                  {onApply && (
                    <Button
                      label={resolvedApplyLabel}
                      onClick={handleApplyClick}
                      size='sm'
                      variant='primary'
                      className={onClear ? 'me-3' : ''}
                    />
                  )}
                  {onClear && (
                    <Button
                      label={resolvedClearLabel}
                      onClick={handleClearClick}
                      size='sm'
                      variant='secondary'
                    />
                  )}
                </div>
                <Button
                  label={resolvedCancelLabel}
                  onClick={handleCancelClick}
                  size='sm'
                  variant='secondary'
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandablePanel;
